"use client";

// Visual for the "Earn 2.68% APY on your deposits" bento card (design handoff:
// interest-landing-animation.md): the APY pill taps, a coin travels a dashed
// elbow into the bank tile, the balance counts up and a "+$504.11 interest"
// chip pops in. A fixed 540x206 stage on a 7s rAF loop, scaled to fit. Below
// 536px the stage is the card width at full size (path and coin stay anchored
// to the right edge) and flows under the card text.

import { ArrowUp } from "iconoir-react";
import * as React from "react";

import { cn } from "@/lib/utils";

import {
  getInterestFrame,
  INTEREST_GAIN,
  INTEREST_PATH,
  INTEREST_REST_SECONDS,
  INTEREST_STAGE,
} from "./interest-timeline";
import { useLoopSeconds, useStageScale } from "./use-stage-loop";

interface InterestVisualProps {
  apyLabel: string;
  interestLabel: string;
  bankAccountLabel: string;
  availableBalanceLabel: string;
  className?: string;
}

const { width, height, pillWidth, elbowX, tileTop } = INTEREST_STAGE;

const cents = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function InterestVisual({
  apyLabel,
  interestLabel,
  bankAccountLabel,
  availableBalanceLabel,
  className,
}: InterestVisualProps) {
  const root = React.useRef<HTMLDivElement>(null);
  const seconds = useLoopSeconds(root, INTEREST_REST_SECONDS);
  const scale = useStageScale(root, width);
  const f = getInterestFrame(seconds);

  return (
    <div
      ref={root}
      aria-hidden
      className={cn(
        "relative mt-auto h-[206px] min-[536px]:absolute min-[536px]:inset-x-6 min-[536px]:mt-0 min-[536px]:h-[calc(var(--s)*206px)] md:inset-x-10",
        className
      )}
      style={{ "--s": scale } as React.CSSProperties}
    >
      <div className="relative mx-auto h-[206px] w-full min-[536px]:w-[540px] min-[536px]:origin-top-left min-[536px]:scale-[var(--s)]">
        <div
          className="border-grey-100 bg-grey-0 absolute top-0 right-0 flex items-center gap-3 rounded-[10px] border py-2.5 pr-5 pl-2.5 shadow-[0_2px_8px_rgba(22,21,23,0.06)]"
          style={{
            width: pillWidth,
            transform: `scale(${f.pillScale})`,
            transformOrigin: "left center",
          }}
        >
          <span className="text-pulse-700 flex size-9 shrink-0 items-center justify-center rounded-full bg-[#e0e6ff] text-[18px] font-semibold">
            %
          </span>
          <span className="text-grey-950 text-[20px] font-medium">
            {apyLabel}
          </span>
          <span className="bg-grey-0 border-grey-950 absolute top-1/2 -left-[5px] -mt-[5px] size-2.5 rounded-full border-[1.5px]" />
        </div>

        <div
          className="pointer-events-none absolute top-0 right-0"
          style={{ width, height }}
        >
          <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            className="absolute inset-0 overflow-visible"
            fill="none"
          >
            <path
              d={INTEREST_PATH}
              stroke="#d9dadb"
              strokeWidth="1.5"
              strokeDasharray="4 4"
            />
            <path
              d={INTEREST_PATH}
              stroke="#001eff"
              strokeWidth="1.5"
              pathLength={1}
              strokeDasharray="1 1"
              strokeDashoffset={f.pathOffset}
            />
          </svg>

          <span
            className="absolute z-10 flex size-[22px] items-center justify-center rounded-full border-2 border-white bg-[#001eff] text-[12px] font-semibold text-white shadow-[0_2px_8px_rgba(0,30,255,0.35)]"
            style={{
              left: f.coin.x - 11,
              top: f.coin.y - 11,
              opacity: f.coin.opacity,
              transform: `scale(${f.coin.scale})`,
            }}
          >
            $
          </span>
        </div>

        <span
          className="absolute inline-flex items-center gap-1.5 rounded-[4px] bg-[#e3f4e8] px-2.5 py-1 text-[13px] font-medium whitespace-nowrap text-[#1f7a39]"
          style={{
            right: width - (elbowX - 14),
            top: 72,
            opacity: f.chip.opacity,
            transform: `translateY(${f.chip.y}px) scale(${f.chip.scale})`,
          }}
        >
          <ArrowUp aria-hidden className="size-3" strokeWidth={2.4} />
          {`+$${cents.format(INTEREST_GAIN)} ${interestLabel}`}
        </span>

        <div
          className="absolute right-0 left-0 box-border h-24 overflow-hidden rounded-[16px] px-[22px] py-[18px] text-white shadow-[0_2px_20px_rgba(22,21,23,0.08)]"
          style={{
            top: tileTop,
            background:
              "linear-gradient(90deg, #001eff 0%, #0a14b8 60%, #000633 100%)",
          }}
        >
          <div
            className="pointer-events-none absolute top-3.5 size-[120px] rounded-full border-2 border-white"
            style={{
              right: width - elbowX,
              opacity: f.ripple.opacity,
              transform: `translate(50%, -50%) scale(${f.ripple.scale})`,
            }}
          />
          <div className="flex h-full items-center justify-between">
            <div className="flex flex-col gap-1.5">
              <span className="flex items-center gap-2 text-[13px] font-medium whitespace-nowrap">
                <span className="flex size-[22px] items-center justify-center rounded-full bg-[#e0e6ff] text-[11px] font-semibold text-[#001eff]">
                  W
                </span>
                {bankAccountLabel}
                <span className="ml-1 hidden text-[11px] font-normal text-[#b9c3ff] tabular-nums min-[420px]:inline">
                  ·· 8323
                </span>
              </span>
              <div className="text-[26px] leading-[1.1] font-medium tracking-[-0.03em] whitespace-nowrap tabular-nums">
                <span className="text-[#b9c3ff]">$</span>
                {cents.format(f.balance)}
              </div>
            </div>
            <div className="text-right text-[11px] leading-[1.5] whitespace-nowrap text-[#b9c3ff]">
              {availableBalanceLabel}
              <br />
              <span className="font-medium text-white">USD</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
